/**
 * EMD Status As On Date Report
 * Displays sales contracts with EMD calculations, received amounts, and balance
 * Sum Total row is shown ONLY on the last page, using grand totals across all pages.
 */
import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaFilter, FaPrint } from 'react-icons/fa';

const SalesEmdReport = () => {
  const [reportData, setReportData]     = useState([]);
  const [grandTotals, setGrandTotals]   = useState({
    total_sale_amount: '0',
    total_emd: '0',
    emd_received_as_on_date: '0',
    balance_emd: '0',
  });
  const [loading, setLoading]           = useState(false);
  const [filters, setFilters]           = useState({
    search: '',
    contractNo: '',
    contractDateFrom: '',
    contractDateTo: '',
    buyer: '',
    commodity: '',
    broker: '',
    asOnDate: new Date().toISOString().split('T')[0],
    excludeBalanceWithin10: false,
    excludeActiveContracts: false,
    emdOverdueOnly: false,
    emdExceedsLedger: false, // ✅ NEW FILTER
  });

  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage]               = useState(1);
  const [totalPages, setTotalPages]   = useState(1);
  const limit = 50;

  const fetchReportData = useCallback(async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);
      if (filters.search)            queryParams.append('search',            filters.search);
      if (filters.contractNo)        queryParams.append('contractNo',        filters.contractNo);
      if (filters.contractDateFrom)  queryParams.append('contractDateFrom',  filters.contractDateFrom);
      if (filters.contractDateTo)    queryParams.append('contractDateTo',    filters.contractDateTo);
      if (filters.buyer)             queryParams.append('buyer',             filters.buyer);
      if (filters.commodity)         queryParams.append('commodity',         filters.commodity);
      if (filters.broker)            queryParams.append('broker',            filters.broker);
      if (filters.asOnDate)          queryParams.append('asOnDate',          filters.asOnDate);
      if (filters.excludeBalanceWithin10) queryParams.append('excludeBalanceWithin10', 'true');
      if (filters.excludeActiveContracts) queryParams.append('excludeActiveContracts', 'true');
      if (filters.emdOverdueOnly)         queryParams.append('emdOverdueOnly',         'true');
      if (filters.emdExceedsLedger)       queryParams.append('emdExceedsLedger',       'true'); // ✅ NEW

      const url = `${API_BASE_URL}/api/report/sales-emd-report?${queryParams.toString()}`;
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
        setGrandTotals(data.grandTotals || {
          total_sale_amount: '0',
          total_emd: '0',
          emd_received_as_on_date: '0',
          balance_emd: '0',
        });
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        toast.error(data.error || 'Failed to fetch report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Server Error');
    }
    setLoading(false);
  }, [page, filters, limit]);

  useEffect(() => {
    fetchReportData();
  }, [fetchReportData]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({
      search: '',
      contractNo: '',
      contractDateFrom: '',
      contractDateTo: '',
      buyer: '',
      commodity: '',
      broker: '',
      asOnDate: new Date().toISOString().split('T')[0],
      excludeBalanceWithin10: false,
      excludeActiveContracts: false,
      emdOverdueOnly: false,
      emdExceedsLedger: false, // ✅ RESET
    });
    setPage(1);
  };

  const handlePrint = () => window.print();
  const handlePrev  = () => setPage((p) => Math.max(1, p - 1));
  const handleNext  = () => setPage((p) => Math.min(totalPages, p + 1));

  const isLastPage = page === totalPages;
  const buyerLedgerRuleActive = filters.emdExceedsLedger;

  return (
    <>
      <style>{`
        @media print {
          @page { size: legal landscape; margin: 12mm 8mm; }
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          body { background: white !important; margin: 0 !important; padding: 0 !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          .shadow-md { box-shadow: none !important; }
          .overflow-x-auto { overflow: visible !important; }
          main { margin-left: 0 !important; width: 100% !important; padding: 0 !important; padding-top: 0 !important; }
          table { width: 100% !important; max-width: 100% !important; border-collapse: collapse !important; page-break-inside: auto; font-size: 9pt !important; }
          th, td { border: 1px solid #333 !important; padding: 3px 5px !important; }
          tr { page-break-inside: avoid !important; break-inside: avoid !important; }
          thead { display: table-header-group !important; }
          .bg-blue-100 { background-color: #dbeafe !important; }
          .bg-gray-50  { background-color: #f3f4f6 !important; }
          .print-report-container { padding: 0 !important; box-shadow: none !important; }
        }
        .print-only { display: none; }
      `}</style>

      <div className="bg-white shadow-md p-6 print-report-container">
        <div className="print-only text-center mb-4" style={{ display: 'none' }}>
          <h2 className="text-xl font-bold text-gray-800 mb-1">
            EMD Status As On{' '}
            {filters.asOnDate
              ? new Date(filters.asOnDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : ''}
          </h2>
        </div>

        <div className="flex justify-between items-center mb-6 no-print">
          <h2 className="text-2xl font-semibold text-gray-800">
            EMD Status As On{' '}
            {filters.asOnDate
              ? new Date(filters.asOnDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
              : ''}
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-[#3899cf] rounded-md hover:bg-blue-200 transition"
            >
              <FaFilter />
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </button>
            <button
              onClick={handlePrint}
              disabled={loading || reportData.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaPrint />
              Print Report
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg no-print">
            <div className="flex flex-wrap gap-4 mb-4">
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Sales Contract No</label>
                <input type="text" value={filters.contractNo} onChange={(e) => handleFilterChange('contractNo', e.target.value)} placeholder="Contract No" className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Date From</label>
                <input type="date" value={filters.contractDateFrom} onChange={(e) => handleFilterChange('contractDateFrom', e.target.value)} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">Contract Date To</label>
                <input type="date" value={filters.contractDateTo} onChange={(e) => handleFilterChange('contractDateTo', e.target.value)} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-52">
                <label className="block text-sm font-medium text-gray-700 mb-1">Buyer</label>
                <input type="text" value={filters.buyer} onChange={(e) => handleFilterChange('buyer', e.target.value)} placeholder="Buyer name" className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Commodity</label>
                <input type="text" value={filters.commodity} onChange={(e) => handleFilterChange('commodity', e.target.value)} placeholder="Commodity" className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-48">
                <label className="block text-sm font-medium text-gray-700 mb-1">Broker</label>
                <input type="text" value={filters.broker} onChange={(e) => handleFilterChange('broker', e.target.value)} placeholder="Broker name" className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-40">
                <label className="block text-sm font-medium text-gray-700 mb-1">As On Date</label>
                <input type="date" value={filters.asOnDate} onChange={(e) => handleFilterChange('asOnDate', e.target.value)} className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
              <div className="flex flex-col w-64">
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input type="text" value={filters.search} onChange={(e) => handleFilterChange('search', e.target.value)} placeholder="Contract No, Buyer, Commodity, Broker" className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" />
              </div>
            </div>

            <div className="border-t border-gray-200 mb-3" />

            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.excludeBalanceWithin10} onChange={(e) => handleFilterChange('excludeBalanceWithin10', e.target.checked)} className="w-4 h-4 accent-[#3899cf] cursor-pointer" />
                <span className="text-sm font-medium text-gray-700">Exclude Balance Qty within (±10%)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.excludeActiveContracts} onChange={(e) => handleFilterChange('excludeActiveContracts', e.target.checked)} className="w-4 h-4 accent-[#3899cf] cursor-pointer" />
                <span className="text-sm font-medium text-gray-700">Show Expired Delivery Contracts Only</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.emdOverdueOnly} onChange={(e) => handleFilterChange('emdOverdueOnly', e.target.checked)} className="w-4 h-4 accent-[#3899cf] cursor-pointer" />
                <span className="text-sm font-medium text-gray-700">Balance EMD Pending (Beyond Trade + 10 Days)</span>
              </label>
              
              {/* ✅ NEW FILTER CHECKBOX */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={filters.emdExceedsLedger} onChange={(e) => handleFilterChange('emdExceedsLedger', e.target.checked)} className="w-4 h-4 accent-[#3899cf] cursor-pointer" />
                <span className="text-sm font-medium text-gray-700">Total EMD Balance &gt; Ledger Balance</span>
              </label>

              <div className="ml-auto">
                <button onClick={resetFilters} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition">
                  Reset Filters
                </button>
              </div>
            </div>

            {buyerLedgerRuleActive && (
              <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900">
                This condition is applied only on EMD Balance &gt; Ledger Balance. If party ledger is higher than buyer total pending EMD, that buyer will not be shown in this report.
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="text-center p-8 text-gray-500">Loading report data...</div>
        )}

        {!loading && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead className="bg-blue-100 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Sales Contract No.</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Contract Date</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Trade Date</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Type of Transaction</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Buyer</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Commodity</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">Qty.</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">Sale Price PMT</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">Total Sale Amount</th>
                  <th className="px-3 py-2 text-left text-gray-700 font-medium border">Broker</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">EMD%</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border align-top">
                    <span>Total EMD</span>
                    <div className="text-xs font-normal text-gray-600 mt-0.5">EMD% × Total Sale Amount</div>
                  </th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">EMD Received As On</th>
                  <th className="px-3 py-2 text-right text-gray-700 font-medium border">Balance EMD</th>
                  {/* ✅ NEW COLUMN - Only show when filter is active */}
                  {filters.emdExceedsLedger && (
                    <th className="px-3 py-2 text-right text-gray-700 font-medium border">Ledger Balance</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reportData.length > 0 ? (
                  <>
                    {reportData.map((item, index) => (
                      <tr key={item.sales_contract_id || index} className="hover:bg-blue-50 transition">
                        <td className="px-3 py-2 border font-medium text-gray-800">{item.sales_contract_no}</td>
                        <td className="px-3 py-2 border text-gray-700">{item.contract_date}</td>
                        <td className="px-3 py-2 border text-gray-700">{item.trade_date}</td>
                        <td className="px-3 py-2 border text-gray-700">{item.type_of_transaction}</td>
                        <td className="px-3 py-2 border text-gray-700">{item.buyer}</td>
                        <td className="px-3 py-2 border text-gray-700">{item.commodity}</td>
                        <td className="px-3 py-2 text-right border text-gray-700">{item.qty}</td>
                        <td className="px-3 py-2 text-right border text-gray-700">{item.sale_price_pmt}</td>
                        <td className="px-3 py-2 text-right border font-semibold text-gray-800">
                          {parseFloat(item.total_sale_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 border text-gray-700">{item.broker}</td>
                        <td className="px-3 py-2 text-right border text-gray-700">{item.emd_percentage}</td>
                        <td className="px-3 py-2 text-right border font-semibold text-gray-800">
                          {parseFloat(item.total_emd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right border text-gray-700">
                          {parseFloat(item.emd_received_as_on_date || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-2 text-right border font-semibold text-gray-800">
                          {Math.max(0, parseFloat(item.balance_emd || 0)).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        {/* ✅ NEW COLUMN DATA */}
                        {filters.emdExceedsLedger && (
                          <td className="px-3 py-2 text-right border text-gray-700">
                            {parseFloat(item.ledger_balance || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                          </td>
                        )}
                      </tr>
                    ))}

                    {isLastPage && (
                      <tr className="bg-gray-50 font-bold">
                        <td colSpan="8" className="px-3 py-3 border text-right text-gray-700">
                          Grand Total
                        </td>
                        <td className="px-3 py-3 text-right border text-gray-800">
                          {parseFloat(grandTotals.total_sale_amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-3 border" />
                        <td className="px-3 py-3 border" />
                        <td className="px-3 py-3 text-right border text-gray-800">
                          {parseFloat(grandTotals.total_emd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-3 text-right border text-gray-800">
                          {parseFloat(grandTotals.emd_received_as_on_date || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-3 py-3 text-right border text-gray-800">
                          {parseFloat(grandTotals.balance_emd || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                        </td>
                        {/* Empty cell for ledger balance column in total row */}
                        {filters.emdExceedsLedger && <td className="px-3 py-3 border" />}
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={filters.emdExceedsLedger ? "15" : "14"} className="text-center p-8 text-gray-500 italic">
                      No data found for the selected filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && reportData.length > 0 && totalPages > 1 && (
          <div className="flex justify-end items-center gap-2 mt-3 text-xs no-print">
            <button onClick={handlePrev} disabled={page === 1} className={`px-2 py-1 rounded border transition-colors duration-100 ${page === 1 ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}>
              Prev
            </button>
            <span className="text-gray-600">Page {page} of {totalPages}</span>
            <button onClick={handleNext} disabled={page === totalPages} className={`px-2 py-1 rounded border transition-colors duration-100 ${page === totalPages ? 'bg-gray-100 text-gray-300 border-gray-200' : 'bg-white text-[#10a5e9] border-[#10a5e9] hover:text-white hover:bg-[#10a5e9]'}`}>
              Next
            </button>
          </div>
        )}

        <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover className="no-print" />
      </div>
    </>
  );
};

export default SalesEmdReport;
